/**
 * The agent bundle contract (workstream 11a; named for its first consumer, the agent studio of 12). An
 * `AgentBundle` is one agent as a pack-shaped file map: data files only (YAML, Markdown, JSON, JSONL), never code.
 * The registry admits bundles at runtime through `agent.bundle.published` (ADR-012); `agentic bundle export`
 * turns an admitted bundle back into a pack folder.
 *
 * Browser-safe like the rest of `@agentic/contracts`: no YAML parser here. Functions that read the YAML files
 * take a `yaml` parser option and default to `Bun.YAML.parse` when it exists.
 */
import { z } from 'zod';
import { AutonomyClass } from './actions.ts';
import { AgentDefinition } from './agent.ts';
import { Id, IsoDateTime, SemVer } from './common.ts';
import { canonicalJson, hashCanonical } from './hash.ts';
import { PolicyBundle } from './policy.ts';

// ---------------------------------------------------------------------------------------------
// Limits, names, paths
// ---------------------------------------------------------------------------------------------

/** Size limits of a bundle (UTF-8 bytes). */
export const BUNDLE_LIMITS = {
  /** Any single file. */
  fileBytes: 64 * 1024,
  /** All files except `evals/*`. */
  totalBytes: 256 * 1024,
  /** All files including `evals/*`. */
  totalWithEvalsBytes: 512 * 1024,
  /** One JSON Schema document (canonical JSON). */
  schemaBytes: 64 * 1024,
} as const;

/** API version of `AgentBundle`. */
export const BUNDLE_API_VERSION = 'agentic.platform/v1';

const NAME = '[a-z0-9][a-z0-9.-]*';

/**
 * Paths a bundle may hold (decision 2): `agents/<name>.yaml`, `prompts/<name>.md`, `schemas/<name>.input.json`,
 * `schemas/<name>.output.json`, `policy/<policy>.rules.yaml`, `policy/tests/<name>.yaml`, `evals/<name>.jsonl`,
 * `evals/scripts/*.json`. Anything else (TypeScript, `module.yaml`, `package.json`) is not bundle data.
 */
export const BUNDLE_FILE_PATH = new RegExp(
  `^(?:agents/${NAME}\\.yaml|prompts/${NAME}\\.md|schemas/${NAME}\\.(?:input|output)\\.json|policy/${NAME}\\.rules\\.yaml|policy/tests/${NAME}\\.yaml|evals/${NAME}\\.jsonl|evals/scripts/[a-z0-9][a-z0-9._-]*\\.json)$`,
);

/** The fixed paths of an agent named `name` (the definition's refs must point at them). */
export function bundlePaths(name: string) {
  return {
    agent: `agents/${name}.yaml`,
    prompt: `prompts/${name}.md`,
    inputSchema: `schemas/${name}.input.json`,
    outputSchema: `schemas/${name}.output.json`,
    policyTests: `policy/tests/${name}.yaml`,
    evals: `evals/${name}.jsonl`,
    policy: (policy: string) => `policy/${policy}.rules.yaml`,
  };
}

/** True for `evals/*` paths (test data: not part of the published bundle, not part of its hash). */
export function isEvalPath(path: string): boolean {
  return path.startsWith('evals/');
}

const encoder = new TextEncoder();
/** UTF-8 byte length of a string. */
export function utf8Bytes(text: string): number {
  return encoder.encode(text).length;
}

/** File map of a bundle: pack-folder path → UTF-8 text. */
export const BundleFiles = z
  .record(z.string(), z.string())
  .superRefine((files, ctx) => {
    let total = 0;
    let totalWithEvals = 0;
    for (const [path, text] of Object.entries(files)) {
      if (!BUNDLE_FILE_PATH.test(path)) {
        ctx.addIssue({
          code: 'custom',
          path: [path],
          message: `${path} is not a bundle data file (agents/, prompts/, schemas/*.json, policy/, evals/ only; never code)`,
        });
      }
      const size = utf8Bytes(text);
      if (size > BUNDLE_LIMITS.fileBytes) {
        ctx.addIssue({
          code: 'custom',
          path: [path],
          message: `file is ${size} bytes (limit ${BUNDLE_LIMITS.fileBytes})`,
        });
      }
      totalWithEvals += size;
      if (!isEvalPath(path)) total += size;
    }
    if (total > BUNDLE_LIMITS.totalBytes) {
      ctx.addIssue({
        code: 'custom',
        message: `bundle is ${total} bytes without evals/* (limit ${BUNDLE_LIMITS.totalBytes})`,
      });
    }
    if (totalWithEvals > BUNDLE_LIMITS.totalWithEvalsBytes) {
      ctx.addIssue({
        code: 'custom',
        message: `bundle is ${totalWithEvals} bytes with evals/* (limit ${BUNDLE_LIMITS.totalWithEvalsBytes})`,
      });
    }
  })
  .meta({ id: 'BundleFiles' });
export type BundleFiles = z.infer<typeof BundleFiles>;

/** The files without `evals/*` (what `agent.bundle.published` carries and the registry stores). */
export function withoutEvals(files: Readonly<Record<string, string>>): Record<string, string> {
  return Object.fromEntries(Object.entries(files).filter(([path]) => !isEvalPath(path)));
}

/**
 * The bundle hash: `hashCanonical(files)` over the files without `evals/*`. For a published bundle (which carries
 * no evals) this is exactly `hashCanonical(files)`; a draft with evals hashes the same as its published form, so an
 * eval result (`agent.bundle.eval.completed`) binds to the bundle that is published.
 */
export function bundleHash(files: Readonly<Record<string, string>>): string {
  return hashCanonical(withoutEvals(files));
}

/** Compares two SemVer strings (`-1`, `0`, `1`); pre-releases sort before their release. Build metadata ignored. */
export function compareSemVer(a: string, b: string): number {
  const split = (v: string) => {
    const [core = '', pre] = v.split('+')[0]?.split(/-(.*)/s) ?? [];
    return { nums: core.split('.').map((n) => Number.parseInt(n, 10) || 0), pre };
  };
  const x = split(a);
  const y = split(b);
  for (let i = 0; i < 3; i++) {
    const d = (x.nums[i] ?? 0) - (y.nums[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  if (x.pre === y.pre) return 0;
  if (x.pre === undefined) return 1;
  if (y.pre === undefined) return -1;
  const xp = x.pre.split('.');
  const yp = y.pre.split('.');
  for (let i = 0; i < Math.max(xp.length, yp.length); i++) {
    const p = xp[i];
    const q = yp[i];
    if (p === undefined) return -1;
    if (q === undefined) return 1;
    const pn = /^\d+$/.test(p) ? Number(p) : undefined;
    const qn = /^\d+$/.test(q) ? Number(q) : undefined;
    if (pn !== undefined && qn !== undefined) {
      if (pn !== qn) return pn < qn ? -1 : 1;
    } else if (pn !== undefined) return -1;
    else if (qn !== undefined) return 1;
    else if (p !== q) return p < q ? -1 : 1;
  }
  return 0;
}

// ---------------------------------------------------------------------------------------------
// JSON Schema subset (decision 3)
// ---------------------------------------------------------------------------------------------

/** JSON types the subset knows. */
export const STUDIO_SCHEMA_TYPES = [
  'string',
  'number',
  'integer',
  'boolean',
  'object',
  'array',
  'null',
] as const;
export type StudioSchemaType = (typeof STUDIO_SCHEMA_TYPES)[number];
/** The four string formats the subset checks. */
export const STUDIO_SCHEMA_FORMATS = ['date', 'date-time', 'email', 'uri'] as const;
export type StudioSchemaFormat = (typeof STUDIO_SCHEMA_FORMATS)[number];

type Primitive = string | number | boolean | null;

/** One node of a `StudioJsonSchema`. Unknown keywords are refused (the schema is then outside the subset). */
export interface StudioJsonSchemaNode {
  $schema?: string;
  title?: string;
  description?: string;
  type?: StudioSchemaType | StudioSchemaType[];
  properties?: Record<string, StudioJsonSchemaNode>;
  required?: string[];
  additionalProperties?: boolean | StudioJsonSchemaNode;
  items?: StudioJsonSchemaNode;
  minItems?: number;
  maxItems?: number;
  enum?: Primitive[];
  const?: Primitive;
  anyOf?: StudioJsonSchemaNode[];
  minimum?: number;
  maximum?: number;
  exclusiveMinimum?: number;
  exclusiveMaximum?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  format?: StudioSchemaFormat;
  default?: unknown;
  examples?: unknown[];
  $defs?: Record<string, StudioJsonSchemaNode>;
  $ref?: string;
}

const PrimitiveSchema = z.union([z.string(), z.number(), z.boolean(), z.null()]);
const Count = z.number().int().nonnegative();
const SchemaType = z.enum(STUDIO_SCHEMA_TYPES);
const LOCAL_REF = /^#\/\$defs\/([A-Za-z0-9_.-]+)$/;

/** Structure of one subset node (strict: unknown keywords fail). */
export const StudioJsonSchemaNode: z.ZodType<StudioJsonSchemaNode> = z.lazy(() =>
  z.strictObject({
    $schema: z.string().optional(),
    title: z.string().max(200).optional(),
    description: z.string().max(4000).optional(),
    type: z.union([SchemaType, z.array(SchemaType).min(1)]).optional(),
    properties: z.record(z.string().min(1).max(200), StudioJsonSchemaNode).optional(),
    required: z.array(z.string().min(1)).optional(),
    additionalProperties: z.union([z.boolean(), StudioJsonSchemaNode]).optional(),
    items: StudioJsonSchemaNode.optional(),
    minItems: Count.optional(),
    maxItems: Count.optional(),
    enum: z.array(PrimitiveSchema).min(1).optional(),
    const: PrimitiveSchema.optional(),
    anyOf: z.array(StudioJsonSchemaNode).min(1).optional(),
    minimum: z.number().optional(),
    maximum: z.number().optional(),
    exclusiveMinimum: z.number().optional(),
    exclusiveMaximum: z.number().optional(),
    minLength: Count.optional(),
    maxLength: Count.optional(),
    pattern: z.string().max(1000).optional(),
    format: z.enum(STUDIO_SCHEMA_FORMATS).optional(),
    default: z.unknown().optional(),
    examples: z.array(z.unknown()).optional(),
    $defs: z.record(z.string().regex(/^[A-Za-z0-9_.-]+$/), StudioJsonSchemaNode).optional(),
    $ref: z.string().regex(LOCAL_REF, 'only local refs #/$defs/<name>').optional(),
  }),
);

/** Problems beyond the node structure: root object, size, local refs resolve, `$defs` at the root only, patterns. */
function subsetProblems(schema: StudioJsonSchemaNode): Array<{ path: string; message: string }> {
  const out: Array<{ path: string; message: string }> = [];
  const size = utf8Bytes(canonicalJson(schema));
  if (size > BUNDLE_LIMITS.schemaBytes) {
    out.push({ path: '$', message: `schema is ${size} bytes (limit ${BUNDLE_LIMITS.schemaBytes})` });
  }
  if (schema.type !== 'object') out.push({ path: '$.type', message: 'the root must be `type: object`' });
  const defs = schema.$defs ?? {};
  const walk = (node: StudioJsonSchemaNode, path: string, root: boolean) => {
    if (!root && node.$defs)
      out.push({ path: `${path}.$defs`, message: '$defs are allowed at the root only' });
    if (node.$ref !== undefined) {
      const name = LOCAL_REF.exec(node.$ref)?.[1];
      if (!name || !(name in defs))
        out.push({ path: `${path}.$ref`, message: `unresolved ref ${node.$ref}` });
      const siblings = Object.keys(node).filter((k) => !['$ref', 'title', 'description'].includes(k));
      if (siblings.length > 0) {
        out.push({ path, message: `$ref takes no sibling keywords (found ${siblings.join(', ')})` });
      }
    }
    if (node.pattern !== undefined) {
      try {
        new RegExp(node.pattern, 'u');
      } catch {
        out.push({ path: `${path}.pattern`, message: 'pattern is not a valid regular expression' });
      }
    }
    for (const [key, child] of Object.entries(node.properties ?? {}))
      walk(child, `${path}.properties.${key}`, false);
    if (typeof node.additionalProperties === 'object')
      walk(node.additionalProperties, `${path}.additionalProperties`, false);
    if (node.items) walk(node.items, `${path}.items`, false);
    for (const [i, child] of (node.anyOf ?? []).entries()) walk(child, `${path}.anyOf[${i}]`, false);
    if (root) for (const [key, child] of Object.entries(defs)) walk(child, `$.$defs.${key}`, false);
  };
  walk(schema, '$', true);
  return out;
}

/**
 * `StudioJsonSchema`: the JSON Schema subset bundle schemas use (types, properties, required, items, enum, const,
 * anyOf, numeric/length/count bounds, pattern, four formats, local `$defs`/`$ref`), with an object root and at most
 * `BUNDLE_LIMITS.schemaBytes`. Validated as data; nothing is compiled or executed.
 */
export const StudioJsonSchema = StudioJsonSchemaNode.superRefine((schema, ctx) => {
  for (const p of subsetProblems(schema))
    ctx.addIssue({ code: 'custom', message: `${p.path}: ${p.message}` });
}).meta({ id: 'StudioJsonSchema' });
export type StudioJsonSchema = StudioJsonSchemaNode;

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const dateTime = z.iso.datetime({ offset: true });
const email = z.email();

function formatHolds(format: StudioSchemaFormat, value: string): boolean {
  switch (format) {
    case 'date':
      return DATE.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`));
    case 'date-time':
      return dateTime.safeParse(value).success;
    case 'email':
      return email.safeParse(value).success;
    case 'uri':
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
  }
}

function literals(values: readonly Primitive[]): z.ZodType {
  const members: z.ZodType[] = values.map((v) => (v === null ? z.null() : z.literal(v)));
  return union(members);
}

function union(members: z.ZodType[]): z.ZodType {
  return members.length === 1 ? (members[0] as z.ZodType) : z.union(members as [z.ZodType, z.ZodType]);
}

/**
 * Deep validator for a subset schema (used by registry admission and the eval runner; agent-runtime keeps its
 * shallow check). Objects without `additionalProperties: false` accept extra keys, as JSON Schema does.
 */
export function toZod(schema: StudioJsonSchema): z.ZodType {
  const defs = schema.$defs ?? {};
  const built = new Map<string, z.ZodType>();
  const refs = new Map<string, z.ZodType>();
  const ref = (name: string): z.ZodType => {
    let lazy = refs.get(name);
    if (!lazy) {
      lazy = z.lazy(() => {
        let target = built.get(name);
        if (!target) {
          const def = defs[name];
          target = def ? build(def) : z.never();
          built.set(name, target);
        }
        return target;
      });
      refs.set(name, lazy);
    }
    return lazy;
  };
  const typed = (type: StudioSchemaType, node: StudioJsonSchemaNode): z.ZodType => {
    switch (type) {
      case 'string': {
        let s = z.string();
        if (node.minLength !== undefined) s = s.min(node.minLength);
        if (node.maxLength !== undefined) s = s.max(node.maxLength);
        if (node.pattern !== undefined) s = s.regex(new RegExp(node.pattern, 'u'));
        const format = node.format;
        return format ? s.refine((v) => formatHolds(format, v), { message: `must be a valid ${format}` }) : s;
      }
      case 'number':
      case 'integer': {
        let n = type === 'integer' ? z.number().int() : z.number();
        if (node.minimum !== undefined) n = n.min(node.minimum);
        if (node.maximum !== undefined) n = n.max(node.maximum);
        if (node.exclusiveMinimum !== undefined) n = n.gt(node.exclusiveMinimum);
        if (node.exclusiveMaximum !== undefined) n = n.lt(node.exclusiveMaximum);
        return n;
      }
      case 'boolean':
        return z.boolean();
      case 'null':
        return z.null();
      case 'array': {
        let a = z.array(node.items ? build(node.items) : z.unknown());
        if (node.minItems !== undefined) a = a.min(node.minItems);
        if (node.maxItems !== undefined) a = a.max(node.maxItems);
        return a;
      }
      case 'object': {
        const required = new Set(node.required ?? []);
        const shape: Record<string, z.ZodType> = {};
        for (const [key, child] of Object.entries(node.properties ?? {})) {
          const s = build(child);
          shape[key] = required.has(key) ? s : s.optional();
        }
        // Required keys without a property schema must still be present.
        for (const key of required)
          if (!(key in shape)) shape[key] = z.unknown().refine((v) => v !== undefined);
        const ap = node.additionalProperties;
        if (ap === false) return z.strictObject(shape);
        if (typeof ap === 'object') return z.object(shape).catchall(build(ap));
        return z.looseObject(shape);
      }
    }
  };
  const build = (node: StudioJsonSchemaNode): z.ZodType => {
    if (node.$ref !== undefined) {
      const name = LOCAL_REF.exec(node.$ref)?.[1];
      return name ? ref(name) : z.never();
    }
    if (node.enum) return literals(node.enum);
    if (node.const !== undefined) return literals([node.const]);
    const types = node.type === undefined ? [] : Array.isArray(node.type) ? node.type : [node.type];
    let base: z.ZodType | undefined = types.length > 0 ? union(types.map((t) => typed(t, node))) : undefined;
    if (node.anyOf) {
      const any = union(node.anyOf.map(build));
      base = base ? z.intersection(base, any) : any;
    }
    return base ?? z.unknown();
  };
  return build(schema);
}

// ---------------------------------------------------------------------------------------------
// Policy test tables (`policy/tests/<name>.yaml`)
// ---------------------------------------------------------------------------------------------

/** One policy test row: a `PolicyInput` built from `input`, and the decision it must produce. */
export const PolicyTestRow = z
  .object({
    id: z.string().regex(/^[a-z0-9][a-z0-9._-]*$/),
    description: z.string().optional(),
    input: z.object({
      tool: z.string().min(1),
      args: z.record(z.string(), z.unknown()).default({}),
      autonomyClass: AutonomyClass,
      domain: z.string().min(1),
      approvalPresent: z.boolean(),
      actorRoles: z.array(z.string()),
      environment: z.string().optional(),
      mode: z.enum(['execute', 'discover']).optional(),
    }),
    expect: z.object({
      allow: z.boolean(),
      /** Each must equal a decision reason, or be the rule id of `<bundle>/<rule id>`. */
      reasonsInclude: z.array(z.string().min(1)).optional(),
      obligationsInclude: z.array(z.string().min(1)).optional(),
    }),
  })
  .meta({ id: 'PolicyTestRow' });
export type PolicyTestRow = z.infer<typeof PolicyTestRow>;

export const PolicyTestTable = z
  .object({
    /** Policy bundle the rows test (defaults to the agent's `spec.policyBundle`). */
    policy: z.string().min(1).optional(),
    rows: z.array(PolicyTestRow).min(1),
  })
  .superRefine((table, ctx) => {
    const seen = new Set<string>();
    for (const [i, row] of table.rows.entries()) {
      if (seen.has(row.id))
        ctx.addIssue({ code: 'custom', path: ['rows', i, 'id'], message: 'duplicate row id' });
      seen.add(row.id);
    }
  })
  .meta({ id: 'PolicyTestTable' });
export type PolicyTestTable = z.infer<typeof PolicyTestTable>;

/** Result of one policy test row (the eval runner reports these). */
export interface PolicyTestRowResult {
  id: string;
  passed: boolean;
  /** Decision actually returned (allow, reasons, obligations). */
  actual: { allow: boolean; reasons: string[]; obligations: string[] };
  detail?: string;
}

/** True when `decision` meets the row's expectation. */
export function policyRowHolds(
  row: PolicyTestRow,
  decision: { allow: boolean; reasons: readonly string[]; obligations: readonly string[] },
): { passed: boolean; detail?: string } {
  const problems: string[] = [];
  if (decision.allow !== row.expect.allow)
    problems.push(`allow ${decision.allow}, expected ${row.expect.allow}`);
  for (const r of row.expect.reasonsInclude ?? []) {
    if (!decision.reasons.some((x) => x === r || x.endsWith(`/${r}`))) problems.push(`reason ${r} missing`);
  }
  for (const o of row.expect.obligationsInclude ?? []) {
    if (!decision.obligations.includes(o)) problems.push(`obligation ${o} missing`);
  }
  return problems.length === 0 ? { passed: true } : { passed: false, detail: problems.join('; ') };
}

// ---------------------------------------------------------------------------------------------
// Evaluations → suites (decision 7)
// ---------------------------------------------------------------------------------------------

/** Suites of `@agentic/core-evals` (`SUITE_RUNNERS` keys), mirrored here so contracts stay core-free. */
export const EVAL_SUITE_KEYS = [
  'dataset',
  'crash-and-resume',
  'stale-approval',
  'tampered-args',
  'injection',
  'acl-isolation',
] as const;
export type EvalSuiteKey = (typeof EVAL_SUITE_KEYS)[number];

/**
 * `spec.evaluations` names a bundle may declare, and the suite each runs. `*-correctness-v1` (any prefix) runs the
 * bundle's dataset (`evals/<name>.jsonl`). A bundle naming anything else is refused.
 */
export const EVAL_SUITE_CATALOG: Readonly<Record<string, EvalSuiteKey>> = {
  '*-correctness-v1': 'dataset',
  'crash-and-resume-v1': 'crash-and-resume',
  'stale-approval-v1': 'stale-approval',
  'tampered-args-v1': 'tampered-args',
  'indirect-prompt-injection-v1': 'injection',
  'acl-isolation-v1': 'acl-isolation',
};

/**
 * `*-correctness-v1` names. 17c0 (review): the charset includes `_` so it covers every name the manifest regex
 * allows (`<module>.<name>` with `[a-z0-9_.-]`): a pack name ending in `-correctness-v1` can never escape the
 * conflict check.
 */
const CORRECTNESS = /^[a-z0-9][a-z0-9._-]*-correctness-v1$/;

/** The suite an evaluation name maps to, or undefined when the catalog does not know it. */
export function evalSuiteFor(evaluation: string): EvalSuiteKey | undefined {
  if (CORRECTNESS.test(evaluation)) return 'dataset';
  if (evaluation.includes('*')) return undefined;
  // 17c0 (review): own keys only, so `constructor` / `__proto__` are not known evaluations.
  return Object.hasOwn(EVAL_SUITE_CATALOG, evaluation) ? EVAL_SUITE_CATALOG[evaluation] : undefined;
}

// ---------------------------------------------------------------------------------------------
// Pack-owned evaluations (17c0, ADR-015 §10)
// ---------------------------------------------------------------------------------------------

/** Where an evaluation catalog entry comes from: the fixed platform catalog or a pack's `provides.evaluations`. */
export const EvalProvenance = z
  .discriminatedUnion('source', [
    z.object({ source: z.literal('platform') }),
    z.object({
      source: z.literal('pack'),
      /** Manifest name of the providing module. */
      module: z.string().min(1),
      /** Manifest version of the providing module. */
      moduleVersion: z.string().optional(),
    }),
  ])
  .meta({ id: 'EvalProvenance' });
export type EvalProvenance = z.infer<typeof EvalProvenance>;

/**
 * One evaluation the catalog knows. Platform entries keep their fixed names (`*-correctness-v1` stays a pattern);
 * pack entries are namespaced `<module>.<name>`. `suite` is a platform suite key (`dataset`, …) or a pack-defined
 * suite kind run by the `EvalSuiteRunner` the pack registered (`registerEvalSuite`).
 */
export const EvalCatalogEntry = z
  .object({
    name: z.string().min(1),
    suite: z.string().min(1),
    provenance: EvalProvenance,
    /** Pack entries: the name inside the manifest (without the `<module>.` prefix). */
    localName: z.string().optional(),
    /** Pack `dataset` entries: JSONL path relative to the module root. */
    datasetRef: z.string().optional(),
  })
  .meta({ id: 'EvalCatalogEntry' });
export type EvalCatalogEntry = z.infer<typeof EvalCatalogEntry>;

/** The suite key of pack evaluations that run the platform dataset runner over the pack's `datasetRef`. */
export const PACK_DATASET_SUITE = 'dataset' satisfies EvalSuiteKey;

/** Catalog name of a pack evaluation: `<module>.<name>`. */
export function packEvaluationName(module: string, name: string): string {
  return `${module}.${name}`;
}

/** The fixed platform entries (`EVAL_SUITE_CATALOG`) with provenance `platform`. */
export function platformEvalEntries(): EvalCatalogEntry[] {
  return Object.entries(EVAL_SUITE_CATALOG).map(([name, suite]) => ({
    name,
    suite,
    provenance: { source: 'platform' },
  }));
}

/** What `packEvalEntries` reads of a module manifest (structural, so callers need not parse a full manifest). */
export interface EvalProvidingManifest {
  metadata: { name: string; version?: string };
  spec: { provides: { evaluations?: ReadonlyArray<{ name: string; suite: string; datasetRef?: string }> } };
}

/** The catalog entries a module manifest declares in `spec.provides.evaluations` (namespaced, in order). */
export function packEvalEntries(manifest: EvalProvidingManifest): EvalCatalogEntry[] {
  const module = manifest.metadata.name;
  return (manifest.spec.provides.evaluations ?? []).map((e) => ({
    name: packEvaluationName(module, e.name),
    suite: e.suite,
    localName: e.name,
    provenance: {
      source: 'pack',
      module,
      ...(manifest.metadata.version ? { moduleVersion: manifest.metadata.version } : {}),
    },
    ...(e.datasetRef ? { datasetRef: e.datasetRef } : {}),
  }));
}

/** A conflict between a pack evaluation and an entry already in the catalog (or a platform name or pattern). */
export interface EvalNameConflict {
  name: string;
  /** The entry that holds the name (`platform` for the fixed catalog and the `*-correctness-v1` pattern). */
  heldBy: string;
  /** The module whose entry was refused. */
  module: string;
}

function holder(entry: EvalCatalogEntry): string {
  return entry.provenance.source === 'platform' ? 'platform' : entry.provenance.module;
}

/**
 * Merges pack entries into a catalog keyed by name. A pack entry conflicts when its name is a platform name, is
 * matched by the `*-correctness-v1` pattern (that would silently change which dataset runs), or is already held by
 * another entry with a different suite, dataset or provider. Identical re-adds are no-ops. Pure: returns the
 * conflicts instead of throwing (`EvaluationCatalog` turns them into `evaluation_name_conflict`).
 */
export function mergeEvalEntries(
  into: Map<string, EvalCatalogEntry>,
  entries: readonly EvalCatalogEntry[],
): EvalNameConflict[] {
  const conflicts: EvalNameConflict[] = [];
  for (const entry of entries) {
    const module = holder(entry);
    if (entry.provenance.source === 'pack' && evalSuiteFor(entry.name) !== undefined) {
      conflicts.push({ name: entry.name, heldBy: 'platform', module });
      continue;
    }
    const held = into.get(entry.name);
    if (held) {
      if (hashCanonical(held) !== hashCanonical(entry)) {
        conflicts.push({ name: entry.name, heldBy: holder(held), module });
      }
      continue;
    }
    into.set(entry.name, entry);
  }
  return conflicts;
}

/**
 * Resolves an evaluation name: the platform catalog first (`evalSuiteFor`, unchanged), then pack entries. The
 * returned platform entry for a `*-correctness-v1` name carries the concrete name, not the pattern.
 */
export function resolveEvaluation(
  name: string,
  packEntries?: { get(name: string): EvalCatalogEntry | undefined },
): EvalCatalogEntry | undefined {
  const suite = evalSuiteFor(name);
  if (suite) return { name, suite, provenance: { source: 'platform' } };
  const entry = packEntries?.get(name);
  return entry && entry.provenance.source === 'pack' ? entry : undefined;
}

// ---------------------------------------------------------------------------------------------
// Bundle, state, origin
// ---------------------------------------------------------------------------------------------

/** Authoring lifecycle of a bundle version (the registry itself stores only `admitted` and `retired`). */
export const BundleState = z
  .enum(['draft', 'evaluating', 'evaluated', 'submitted', 'published', 'admitted', 'refused', 'retired'])
  .meta({ id: 'BundleState' });
export type BundleState = z.infer<typeof BundleState>;

/** Where a bundle came from. */
export const BundleOrigin = z
  .object({
    kind: z.enum(['blank', 'template', 'agent', 'ai', 'import']),
    /** Template id, source agent name@version, import folder, … */
    ref: z.string().max(500).optional(),
    /** Task that generated it (`ai`). */
    taskId: Id.optional(),
  })
  .meta({ id: 'BundleOrigin' });
export type BundleOrigin = z.infer<typeof BundleOrigin>;

const AgentName = z.string().regex(/^[a-z0-9][a-z0-9.-]*$/, 'agent name must be lowercase kebab/dot case');

/**
 * One agent as a file map. `definition` equals the parsed `agents/<name>.yaml` and its refs point at the fixed
 * paths (`bundlePaths`); `hash = bundleHash(files)`. Check content with `parseBundle`.
 */
export const AgentBundle = z
  .object({
    apiVersion: z.literal(BUNDLE_API_VERSION),
    kind: z.literal('AgentBundle'),
    /** Stable across versions of one bundle (the authoring draft id). */
    bundleId: z.string().min(1).max(100),
    name: AgentName,
    version: SemVer,
    definition: AgentDefinition,
    files: BundleFiles,
    origin: BundleOrigin,
    hash: z.string().regex(/^[0-9a-f]{64}$/, 'sha-256 hex'),
  })
  .meta({ id: 'AgentBundle' });
export type AgentBundle = z.infer<typeof AgentBundle>;
export type AgentBundleInput = z.input<typeof AgentBundle>;

/** A problem found in a bundle, with the file (or field) it is about. */
export interface BundleIssue {
  path: string;
  code: string;
  message: string;
}

export type BundleParseResult = { ok: true; bundle: AgentBundle } | { ok: false; issues: BundleIssue[] };

export interface ParseBundleOptions {
  /** YAML parser for agent, policy and policy-test files; defaults to `Bun.YAML.parse` when available. */
  yaml?: (text: string) => unknown;
}

function defaultYaml(): ((text: string) => unknown) | undefined {
  const bun = (globalThis as { Bun?: { YAML?: { parse(text: string): unknown } } }).Bun;
  const parse = bun?.YAML?.parse;
  return parse ? (text: string) => parse.call(bun?.YAML, text) : undefined;
}

function zodIssues(error: z.ZodError, prefix: string, code: string): BundleIssue[] {
  return error.issues.map((i) => ({
    path: [prefix, ...i.path.map(String)].filter(Boolean).join('.') || '$',
    code,
    message: i.message,
  }));
}

/** Content checks of a structurally valid bundle (shared by `parseBundle` and `bundleFromFiles`). */
function contentIssues(
  files: Readonly<Record<string, string>>,
  definition: AgentDefinition,
  name: string,
  version: string,
  yaml: ((text: string) => unknown) | undefined,
): BundleIssue[] {
  const issues: BundleIssue[] = [];
  const add = (path: string, code: string, message: string) => issues.push({ path, code, message });
  const paths = bundlePaths(name);
  const parseYaml = (path: string): unknown => {
    if (!yaml) return undefined;
    try {
      return yaml(files[path] ?? '');
    } catch (error) {
      add(path, 'yaml', `invalid YAML: ${(error as Error).message}`);
      return undefined;
    }
  };
  const has = (path: string) => Object.hasOwn(files, path);

  // Agent file and definition.
  const agentFiles = Object.keys(files).filter((p) => p.startsWith('agents/'));
  if (!has(paths.agent)) add(paths.agent, 'missing_file', 'the agent definition file is missing');
  for (const p of agentFiles)
    if (p !== paths.agent) add(p, 'unexpected_file', `one agent per bundle (${name})`);
  if (definition.metadata.name !== name)
    add('definition.metadata.name', 'definition_mismatch', `must be ${name}`);
  if (definition.metadata.version !== version) {
    add('definition.metadata.version', 'definition_mismatch', `must be ${version}`);
  }
  if (has(paths.agent) && yaml) {
    const raw = parseYaml(paths.agent);
    if (raw !== undefined) {
      const parsed = AgentDefinition.safeParse(raw);
      if (!parsed.success) issues.push(...zodIssues(parsed.error, paths.agent, 'agent_definition'));
      else if (canonicalJson(parsed.data) !== canonicalJson(definition)) {
        add('definition', 'definition_mismatch', `definition differs from ${paths.agent}`);
      }
    }
  }

  // Refs point at the fixed paths.
  const { spec } = definition;
  const refs: Array<[string, string, string]> = [
    ['spec.prompt.systemRef', spec.prompt.systemRef, paths.prompt],
    ['spec.inputSchemaRef', spec.inputSchemaRef, paths.inputSchema],
    ['spec.outputSchemaRef', spec.outputSchemaRef, paths.outputSchema],
  ];
  for (const [field, actual, expected] of refs) {
    if (actual !== expected) {
      const ts = /\.(m?ts|tsx|m?js)(#.*)?$/.test(actual);
      add(
        field,
        ts ? 'typescript_ref' : 'ref_path',
        ts
          ? `${actual} is code; bundles use JSON Schema (${expected}). Agents with TypeScript schemas stay git packs`
          : `must be ${expected}`,
      );
    } else if (!has(expected)) add(expected, 'missing_file', `${field} points at a missing file`);
  }
  for (const p of Object.keys(files)) {
    if (p.startsWith('prompts/') && p !== paths.prompt)
      add(p, 'unexpected_file', 'not referenced by the agent');
    if (p.startsWith('schemas/') && p !== paths.inputSchema && p !== paths.outputSchema) {
      add(p, 'unexpected_file', 'not referenced by the agent');
    }
  }

  // Schemas: the JSON Schema subset.
  for (const p of [paths.inputSchema, paths.outputSchema]) {
    if (!has(p)) continue;
    let json: unknown;
    try {
      json = JSON.parse(files[p] ?? '');
    } catch (error) {
      add(p, 'schema_json', `invalid JSON: ${(error as Error).message}`);
      continue;
    }
    const parsed = StudioJsonSchema.safeParse(json);
    if (!parsed.success) issues.push(...zodIssues(parsed.error, p, 'schema_subset'));
  }

  // Owned policy.
  const policyPath = paths.policy(spec.policyBundle);
  const policyFiles = Object.keys(files).filter((p) => /^policy\/[^/]+\.rules\.yaml$/.test(p));
  if (!has(policyPath)) add(policyPath, 'missing_policy', `the owned policy ${spec.policyBundle} is missing`);
  for (const p of policyFiles) if (p !== policyPath) add(p, 'unexpected_file', 'one owned policy per bundle');
  if (has(policyPath) && yaml) {
    const raw = parseYaml(policyPath);
    if (raw !== undefined) {
      const parsed = PolicyBundle.safeParse(raw);
      if (!parsed.success) issues.push(...zodIssues(parsed.error, policyPath, 'policy'));
      else {
        if (parsed.data.name !== spec.policyBundle)
          add(policyPath, 'policy', `name must be ${spec.policyBundle}`);
        if (!SemVer.safeParse(parsed.data.version).success)
          add(policyPath, 'policy', 'version must be SemVer');
      }
    }
  }

  // Policy tests.
  for (const p of Object.keys(files).filter((x) => x.startsWith('policy/tests/'))) {
    if (p !== paths.policyTests) {
      add(p, 'unexpected_file', `policy tests live in ${paths.policyTests}`);
      continue;
    }
    if (!yaml) continue;
    const raw = parseYaml(p);
    if (raw === undefined) continue;
    const parsed = PolicyTestTable.safeParse(raw);
    if (!parsed.success) issues.push(...zodIssues(parsed.error, p, 'policy_tests'));
    else if (parsed.data.policy && parsed.data.policy !== spec.policyBundle) {
      add(p, 'policy_tests', `tests policy ${parsed.data.policy}, the agent owns ${spec.policyBundle}`);
    }
  }

  // Eval cases (deep validation of each case is the runner's: `@agentic/core-evals` EvalCase).
  for (const p of Object.keys(files).filter((x) => /^evals\/[^/]+\.jsonl$/.test(x))) {
    if (p !== paths.evals) {
      add(p, 'unexpected_file', `eval cases live in ${paths.evals}`);
      continue;
    }
    for (const [i, raw] of (files[p] ?? '').split('\n').entries()) {
      const line = raw.trim();
      if (!line || line.startsWith('//')) continue;
      let json: unknown;
      try {
        json = JSON.parse(line);
      } catch (error) {
        add(`${p}:${i + 1}`, 'evals', `invalid JSON: ${(error as Error).message}`);
        continue;
      }
      const c = json as { id?: unknown; scriptRef?: unknown } | null;
      if (typeof c !== 'object' || c === null || typeof c.id !== 'string') {
        add(`${p}:${i + 1}`, 'evals', 'each line is an eval case object with an id');
        continue;
      }
      if (c.scriptRef !== undefined && (typeof c.scriptRef !== 'string' || !has(c.scriptRef))) {
        add(`${p}:${i + 1}`, 'evals', `scriptRef ${String(c.scriptRef)} is not a file of the bundle`);
      }
    }
  }
  for (const p of Object.keys(files).filter((x) => x.startsWith('evals/scripts/'))) {
    try {
      if (!Array.isArray(JSON.parse(files[p] ?? ''))) add(p, 'evals', 'a model script is a JSON array');
    } catch (error) {
      add(p, 'evals', `invalid JSON: ${(error as Error).message}`);
    }
  }
  return issues;
}

/**
 * Validates a bundle: shape (paths, sizes), hash, the definition against `agents/<name>.yaml`, refs at the fixed
 * paths, schemas inside the subset, the owned policy, policy tests and eval case lines. Never executes anything.
 */
export function parseBundle(input: unknown, options: ParseBundleOptions = {}): BundleParseResult {
  const parsed = AgentBundle.safeParse(input);
  if (!parsed.success) return { ok: false, issues: zodIssues(parsed.error, '', 'shape') };
  const bundle = parsed.data;
  const issues: BundleIssue[] = [];
  const expected = bundleHash(bundle.files);
  if (bundle.hash !== expected) {
    issues.push({ path: 'hash', code: 'hash_mismatch', message: `hash ${bundle.hash} != ${expected}` });
  }
  issues.push(
    ...contentIssues(
      bundle.files,
      bundle.definition,
      bundle.name,
      bundle.version,
      options.yaml ?? defaultYaml(),
    ),
  );
  return issues.length > 0 ? { ok: false, issues } : { ok: true, bundle };
}

/**
 * Builds a bundle from a file map (an imported pack folder or an authoring draft): name, version and definition
 * come from the single `agents/*.yaml`, the hash is computed. Paths that are not bundle data are dropped by the
 * caller (`agentic bundle import` reads data files only).
 */
export function bundleFromFiles(
  files: Readonly<Record<string, string>>,
  meta: { bundleId: string; origin: BundleOrigin },
  options: ParseBundleOptions = {},
): BundleParseResult {
  const yaml = options.yaml ?? defaultYaml();
  if (!yaml)
    return { ok: false, issues: [{ path: 'agents', code: 'yaml', message: 'no YAML parser available' }] };
  const agentFiles = Object.keys(files).filter((p) => /^agents\/[^/]+\.yaml$/.test(p));
  if (agentFiles.length !== 1) {
    return {
      ok: false,
      issues: [
        {
          path: 'agents',
          code: 'agent_file',
          message: `expected one agents/*.yaml, found ${agentFiles.length}`,
        },
      ],
    };
  }
  const agentPath = agentFiles[0] as string;
  let raw: unknown;
  try {
    raw = yaml(files[agentPath] ?? '');
  } catch (error) {
    return { ok: false, issues: [{ path: agentPath, code: 'yaml', message: (error as Error).message }] };
  }
  const definition = AgentDefinition.safeParse(raw);
  if (!definition.success)
    return { ok: false, issues: zodIssues(definition.error, agentPath, 'agent_definition') };
  const candidate = {
    apiVersion: BUNDLE_API_VERSION,
    kind: 'AgentBundle' as const,
    bundleId: meta.bundleId,
    name: definition.data.metadata.name,
    version: definition.data.metadata.version,
    definition: definition.data,
    files: { ...files },
    origin: meta.origin,
    hash: bundleHash(files),
  };
  return parseBundle(candidate, { yaml });
}

/** The published form of a bundle: no `evals/*` (same hash). */
export function publishedBundle(bundle: AgentBundle): AgentBundle {
  return { ...bundle, files: withoutEvals(bundle.files) };
}

// ---------------------------------------------------------------------------------------------
// Permissions, groups, audit kinds
// ---------------------------------------------------------------------------------------------

/** Role permissions for authoring (decision 4). `admin` holds all three, `agent-author` holds `studio.draft`. */
export const STUDIO_PERMISSIONS = {
  draft: 'studio.draft',
  publish: 'studio.publish',
  retire: 'studio.retire',
} as const;

/** Consumer groups of the bundle events. */
export const BUNDLE_CONSUMER_GROUPS = {
  /** Registry admission (`agent.bundle.published`, `agent.bundle.retired`). */
  admission: 'registry.bundles',
  /** The eval gate runner (`agent.bundle.eval.requested`; apps/platform, worker processes). */
  evals: 'registry.bundle-evals',
} as const;

/** `audit.entry` kinds written by `agentic bundle import|retire`. */
export const BUNDLE_AUDIT_KINDS = {
  imported: 'bundle.imported',
  retired: 'bundle.retired',
} as const;

// ---------------------------------------------------------------------------------------------
// Events (subject = bundle name)
// ---------------------------------------------------------------------------------------------

export const BUNDLE_EVENT_TYPES = {
  evalRequested: 'agent.bundle.eval.requested',
  evalCompleted: 'agent.bundle.eval.completed',
  published: 'agent.bundle.published',
  retired: 'agent.bundle.retired',
  catalogChanged: 'registry.catalog.changed',
} as const;

/** `agent.bundle.eval.requested`: evaluate this bundle (with its `evals/*`). Idempotency: `evalRunId`. */
export const BundleEvalRequestedData = z
  .object({
    evalRunId: z.string().min(1).max(100),
    bundle: AgentBundle,
    requestedBy: z.string().min(1),
    /** Who expects the `.completed` event (e.g. the `studio.eval-results` group of 12). */
    replyTo: z.string().max(200).optional(),
    at: IsoDateTime,
  })
  .meta({ id: 'BundleEvalRequestedData' });
export type BundleEvalRequestedData = z.infer<typeof BundleEvalRequestedData>;

/** At most this many case results per suite (failed and error cases first). */
export const BUNDLE_EVAL_MAX_CASE_RESULTS = 64;
/** At most this many checks (and violations) per case result. */
export const BUNDLE_EVAL_MAX_CASE_CHECKS = 64;
/** Grader `detail` strings are cut to this many characters. */
export const BUNDLE_EVAL_MAX_DETAIL = 500;

const EvalDetail = z.string().max(BUNDLE_EVAL_MAX_DETAIL);

/** One grader check of an eval case (`status`, `outcome`, `executed:<tool>`, `cites:<ref>`, …). */
export const BundleEvalCaseCheck = z
  .object({
    name: z.string().min(1).max(200),
    passed: z.boolean(),
    detail: EvalDetail.optional(),
  })
  .meta({ id: 'BundleEvalCaseCheck' });
export type BundleEvalCaseCheck = z.infer<typeof BundleEvalCaseCheck>;

/** One critical-property finding of an eval case (`unauthorized_action`, `duplicate_effect`, …). */
export const BundleEvalCaseViolation = z
  .object({
    kind: z.string().min(1).max(100),
    critical: z.boolean(),
    detail: EvalDetail.optional(),
  })
  .meta({ id: 'BundleEvalCaseViolation' });
export type BundleEvalCaseViolation = z.infer<typeof BundleEvalCaseViolation>;

/**
 * Per-case result of one suite: why a case failed, without the eval copy database. `error` = the case never
 * ran (intake refused it, the `created` check).
 */
export const BundleEvalCaseResult = z
  .object({
    id: z.string().min(1).max(200),
    status: z.enum(['passed', 'failed', 'error']),
    taskId: z.string().max(100).optional(),
    checks: z.array(BundleEvalCaseCheck).max(BUNDLE_EVAL_MAX_CASE_CHECKS).default([]),
    violations: z.array(BundleEvalCaseViolation).max(BUNDLE_EVAL_MAX_CASE_CHECKS).default([]),
  })
  .meta({ id: 'BundleEvalCaseResult' });
export type BundleEvalCaseResult = z.infer<typeof BundleEvalCaseResult>;
export type BundleEvalCaseResultInput = z.input<typeof BundleEvalCaseResult>;

/** Compact per-suite outcome carried by `.eval.completed` (the full report is identified by `reportHash`). */
export const BundleEvalSuiteSummary = z
  .object({
    name: z.string(),
    status: z.enum(['passed', 'failed', 'skipped']),
    reason: z.string().optional(),
    /** Number of cases the suite ran (all of them, also when `caseResults` is cut). */
    cases: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    /**
     * Per-case checks and violations, failed and error cases first, at most `BUNDLE_EVAL_MAX_CASE_RESULTS`.
     * Absent on reports stored before 2026-11-02.
     */
    caseResults: z.array(BundleEvalCaseResult).max(BUNDLE_EVAL_MAX_CASE_RESULTS).optional(),
  })
  .meta({ id: 'BundleEvalSuiteSummary' });

/** `agent.bundle.eval.completed`: the gate result. Idempotency: `evalRunId` + `hash`. */
export const BundleEvalCompletedData = z
  .object({
    evalRunId: z.string().min(1).max(100),
    bundleId: z.string().min(1),
    name: z.string().min(1),
    version: z.string().min(1),
    hash: z.string().min(1),
    /** `gate(report, { strict: true }).ok` and every policy test row passed. */
    passed: z.boolean(),
    status: z.enum(['passed', 'failed', 'error']),
    /** `hashCanonical(report)` of the full `EvalReport`. */
    reportHash: z.string().optional(),
    summary: z
      .object({
        cases: z.number().int().nonnegative(),
        passed: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
        skippedSuites: z.number().int().nonnegative(),
        criticalViolations: z.number().int().nonnegative(),
      })
      .optional(),
    suites: z.array(BundleEvalSuiteSummary).default([]),
    gateReasons: z.array(z.string()).default([]),
    policyTests: z
      .object({
        rows: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
        failures: z.array(z.object({ id: z.string(), detail: z.string() })).default([]),
      })
      .optional(),
    /** `status: error`: why no report was produced (`tool_without_eval_mock`, `hooks_ambiguous`, `timeout`, …). */
    error: z.object({ code: z.string(), message: z.string() }).optional(),
    durationMs: z.number().nonnegative(),
    replyTo: z.string().optional(),
    at: IsoDateTime,
  })
  .meta({ id: 'BundleEvalCompletedData' });
export type BundleEvalCompletedData = z.infer<typeof BundleEvalCompletedData>;
export type BundleEvalCompletedInput = z.input<typeof BundleEvalCompletedData>;

/** `agent.bundle.published`: admit this bundle (no `evals/*`). Idempotency: name@version + hash. */
export const BundlePublishedData = z
  .object({
    bundle: AgentBundle.refine((b) => Object.keys(b.files).every((p) => !isEvalPath(p)), {
      message: 'a published bundle carries no evals/* files',
    }),
    publishedBy: z.string().min(1),
    /** The passing eval run, when one gated the publish. */
    evalRunId: z.string().max(100).optional(),
    at: IsoDateTime,
  })
  .meta({ id: 'BundlePublishedData' });
export type BundlePublishedData = z.infer<typeof BundlePublishedData>;

/**
 * `agent.bundle.retired`: retire a bundle agent. Retirement is by name: the agent kill switch engages and every
 * admitted version is marked retired; `version` records the version the retirer saw (informational).
 */
export const BundleRetiredData = z
  .object({
    name: AgentName,
    /** The version the retirer saw (informational; every version of the name is retired). */
    version: SemVer.optional(),
    bundleId: z.string().optional(),
    retiredBy: z.string().min(1),
    reason: z.string().min(1).max(500),
    at: IsoDateTime,
  })
  .meta({ id: 'BundleRetiredData' });
export type BundleRetiredData = z.infer<typeof BundleRetiredData>;

/** `registry.catalog.changed`: an admission decision or a retirement (published by the registry). */
export const RegistryCatalogChangedData = z
  .object({
    change: z.enum(['admitted', 'refused', 'retired']),
    name: z.string().min(1),
    version: z.string().optional(),
    hash: z.string().optional(),
    bundleId: z.string().optional(),
    /** Refusal or retirement reason (`code: message`). */
    reason: z.string().optional(),
    /** The `agent.bundle.*` event this decision answers. */
    causationEventId: z.string().min(1),
    at: IsoDateTime,
  })
  .meta({ id: 'RegistryCatalogChangedData' });
export type RegistryCatalogChangedData = z.infer<typeof RegistryCatalogChangedData>;
